import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Dialog, IconButton, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import Papa from 'papaparse';
import * as Sentry from '@sentry/react';
import { getSecuritySettingsConfig } from '../../../api/firebase/firestore/security/read';
import { updateSecurityIpAllowlist } from '../../../api/firebase/function/security/securityIpFunction';
import type { IpAllowlistEntry } from '../../../types/security';
import { SECURITY_IP_ALLOWLIST_MAX } from '../../../constants/enum';
import CustomTextfield from '../../uiParts/form/CustomTextfield';
import CustomLoading from '../../uiParts/form/CustomLoading';
import {
  gray40,
  gray80,
  gray100,
  green50,
  primaryButtonBackgroundColor,
  red20,
  red50,
  tertiaryButtonBackgroundColor,
} from '../../../constants/colors';
import { dialogText, dialogTitle, regularSm, regularXs } from '../../../constants/location';

interface Props {
  /** 代理モード / 運営時の対象企業。companyAdmin 自身は未指定でよい */
  companyId?: string;
}

// IPv4 CIDR（/0〜/32 は任意）の簡易バリデーション。最終検証はサーバー側で行う。
const isValidIpv4Cidr = (value: string): boolean => {
  const m = value
    .trim()
    .match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})(?:\/(\d|[12]\d|3[0-2]))?$/);
  if (!m) return false;
  return [m[1], m[2], m[3], m[4]].every(o => Number(o) <= 255);
};

const styles = {
  // .App のグローバル text-align: center を打ち消す（本文は左揃え基本・design-rules §1）
  wrapper: {
    textAlign: 'left' as const,
  },
  description: {
    ...regularSm,
    color: gray100,
    marginBottom: '8px',
  },
  caption: {
    ...regularXs,
    color: gray80,
    marginBottom: '16px',
  },
  infoText: {
    ...regularSm,
    color: green50,
    marginBottom: '12px',
  },
  toolbar: {
    display: 'flex',
    gap: '12px',
    marginBottom: '16px',
    flexWrap: 'wrap',
  },
  toolbarButton: {
    height: '40px',
  },
  errorText: {
    ...regularSm,
    color: red50,
    marginBottom: '12px',
  },
  row: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    marginBottom: '8px',
  },
  cidrCol: {
    width: '220px',
  },
  noteCol: {
    flex: 1,
    minWidth: '200px',
  },
  rowError: {
    ...regularXs,
    color: red50,
    marginTop: '2px',
  },
  deleteButton: {
    marginTop: '4px',
  },
  emptyText: {
    ...regularSm,
    color: gray80,
    padding: '16px 0',
  },
  countText: {
    ...regularXs,
    color: gray80,
    marginTop: '8px',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    marginTop: '24px',
    borderTop: '1px solid' + gray40,
    paddingTop: '16px',
  },
  saveButton: {
    height: '44px',
    minWidth: '120px',
  },
  // 確認ダイアログ
  dialog: {
    '& .MuiPaper-root': {
      border: '1px solid' + gray40,
      boxSizing: 'border-box',
      margin: '16px',
      padding: '40px',
      width: '480px',
    },
  },
  dialogHeader: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '24px',
  },
  dialogTitle,
  warnBox: {
    ...regularSm,
    backgroundColor: red20,
    border: `1px solid ${red50}`,
    borderRadius: '4px',
    color: red50,
    padding: '12px',
    marginBottom: '16px',
  },
  dialogText,
  dialogActions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    marginTop: '24px',
  },
  dialogButton: {
    height: '44px',
    minWidth: '120px',
  },
};

/**
 * IP アドレス制限タブ（#2982）。設定 > セキュリティ > IP アドレス制限。
 * サイト（画面）へのアクセスを許可する IP/CIDR のテナント許可リストを編集する。
 * 一覧 / 追加 / 編集 / 削除 + CSV インポート。保存時にロックアウト防止セーフガードが働く。
 * アクセス遮断（enforcement）はサーバー側の共通ガード（middleware/screenIpRestriction）と
 * フロントの 403 ゲート（IpRestrictionGate）が行う。
 */
const IpRestrictionTab: React.FC<Props> = ({ companyId }) => {
  const [entries, setEntries] = useState<IpAllowlistEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [infoText, setInfoText] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [callerIp, setCallerIp] = useState('');
  // 運営による一時無効化中フラグ（#2982。表示のみ・切替は運営_企業詳細）
  const [bypassActive, setBypassActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setErrorText('');
    try {
      const config = await getSecuritySettingsConfig(companyId ?? '');
      setEntries(config?.ip_allowlist ?? []);
      setBypassActive(config?.ip_restriction_disabled === true);
    } catch (e) {
      Sentry.captureException(e);
      setErrorText(e instanceof Error ? e.message : 'IP 許可リストの取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    // companyId が未確定（自社 companyAdmin）でも Firestore ルールが自社を解決するが、
    // ここでは companyId が空の場合サーバー callable 側で自社に解決される。読み取りは companyId が必要。
    if (companyId) {
      void load();
    } else {
      // 自社 companyAdmin の companyId は上位から渡らないケースがあるため、その場合は空で開始
      setLoading(false);
    }
  }, [companyId, load]);

  const addRow = (): void => {
    if (entries.length >= SECURITY_IP_ALLOWLIST_MAX) return;
    setEntries(prev => [...prev, { cidr: '', note: '' }]);
  };

  const updateRow = (index: number, patch: Partial<IpAllowlistEntry>): void => {
    setEntries(prev => prev.map((e, i) => (i === index ? { ...e, ...patch } : e)));
  };

  const removeRow = (index: number): void => {
    setEntries(prev => prev.filter((_, i) => i !== index));
  };

  const handleImportClick = (): void => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    // 同じファイルを連続選択しても発火するようリセット
    e.target.value = '';
    if (!file) return;
    Papa.parse<string[]>(file, {
      skipEmptyLines: true,
      complete: (result): void => {
        const rows = result.data;
        // ヘッダー行の除外は「先頭列が CIDR / IP として解釈できないか」で判定する（#3103）。
        // 想定ヘッダーは cidr だが、ip / IPアドレス 等の別名でも不正な CIDR 行として
        // 取り込まれないようにする（データ行の先頭列は必ず CIDR / IP のため誤判定しない）
        const hasHeaderRow = rows.length > 0 && !isValidIpv4Cidr(rows[0]?.[0] ?? '');
        const imported: IpAllowlistEntry[] = (hasHeaderRow ? rows.slice(1) : rows)
          .map(row => ({
            cidr: (row[0] ?? '').trim(),
            note: (row[1] ?? '').trim(),
          }))
          .filter(r => r.cidr);
        // 件数表示は上限適用「後」の実際に取り込まれた行数を出す（既存+CSVが上限超過時のズレ防止）
        const merged = [...entries, ...imported].slice(0, SECURITY_IP_ALLOWLIST_MAX);
        const added = merged.length - entries.length;
        const dropped = imported.length - added;
        setEntries(merged);
        const message =
          dropped > 0
            ? `${added} 件を読み込みました（上限 ${SECURITY_IP_ALLOWLIST_MAX} 件を超えた ${dropped} 件は取り込まれていません）。保存で反映されます。`
            : `${added} 件を読み込みました（保存で反映されます）。`;
        // 除外した行をユーザーが把握できるようにする（データ行の取りこぼしに気付けるため）
        setInfoText(hasHeaderRow ? `${message}1 行目はヘッダー行として除外しています。` : message);
      },
      error: (err): void => {
        Sentry.captureException(err);
        setErrorText('CSV の読み込みに失敗しました。');
      },
    });
  };

  // 保存前のクライアント検証（重複・形式・件数）。エラーがあればメッセージを返す。
  const validate = (): string => {
    const filled = entries.filter(e => e.cidr.trim());
    if (filled.length > SECURITY_IP_ALLOWLIST_MAX) {
      return `IP 許可リストは ${SECURITY_IP_ALLOWLIST_MAX} 件までです。`;
    }
    const invalid = filled.filter(e => !isValidIpv4Cidr(e.cidr));
    if (invalid.length > 0) {
      return `IPv4 CIDR の形式が不正な行があります: ${invalid.map(e => e.cidr).join(', ')}`;
    }
    const cidrs = filled.map(e => e.cidr.trim());
    const dup = cidrs.find((c, i) => cidrs.indexOf(c) !== i);
    if (dup) return `重複した CIDR があります: ${dup}`;
    return '';
  };

  const save = async (force: boolean): Promise<void> => {
    setSaving(true);
    setErrorText('');
    setInfoText('');
    try {
      const payload: IpAllowlistEntry[] = entries
        .filter(e => e.cidr.trim())
        .map(e =>
          e.note?.trim() ? { cidr: e.cidr.trim(), note: e.note.trim() } : { cidr: e.cidr.trim() },
        );
      const result = await updateSecurityIpAllowlist({
        company_id: companyId,
        ip_allowlist: payload,
        force,
      });
      if (result.requires_confirmation && !force) {
        setCallerIp(result.caller_ip);
        setConfirmOpen(true);
        return;
      }
      setConfirmOpen(false);
      setInfoText('IP 許可リストを保存しました。');
      await load();
    } catch (e) {
      Sentry.captureException(e);
      setConfirmOpen(false);
      setErrorText(e instanceof Error ? e.message : 'IP 許可リストの保存に失敗しました。');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = (): void => {
    const validationError = validate();
    if (validationError) {
      setErrorText(validationError);
      return;
    }
    void save(false);
  };

  if (loading) {
    return <CustomLoading loading />;
  }

  const filledCount = entries.filter(e => e.cidr.trim()).length;

  return (
    <Box sx={styles.wrapper}>
      <Typography sx={styles.description}>
        許可した IP アドレス（CIDR 表記）からのみ Gen Drive にアクセスできるようにします。許可外の
        IP からのアクセスは 403 になります。
      </Typography>
      <Typography sx={styles.caption}>
        IPv4 のみ対応（IPv6 は今後対応）。最大 {SECURITY_IP_ALLOWLIST_MAX} 件。
        保存時に現在のあなたの IP が含まれるか検証します（ロックアウト防止）。
        リストが空の場合は制限されません。設定変更は最大 5 分で全体に反映されます。
        企業管理者と運営スタッフは締め出し防止のため、当面は制限の対象外です。
      </Typography>

      {bypassActive && (
        <Typography sx={styles.errorText}>
          現在、運営によって IP
          制限が一時無効化されています（許可リストによる遮断は行われていません）。解除は運営にお問い合わせください。
        </Typography>
      )}

      {errorText && <Typography sx={styles.errorText}>{errorText}</Typography>}
      {infoText && <Typography sx={styles.infoText}>{infoText}</Typography>}

      <Box sx={styles.toolbar}>
        <Button
          onClick={addRow}
          disabled={entries.length >= SECURITY_IP_ALLOWLIST_MAX}
          startIcon={<AddIcon sx={{ height: '18px', width: '18px' }} />}
          sx={[tertiaryButtonBackgroundColor, styles.toolbarButton]}
        >
          行を追加
        </Button>
        <Button
          onClick={handleImportClick}
          startIcon={<UploadFileIcon sx={{ height: '18px', width: '18px' }} />}
          sx={[tertiaryButtonBackgroundColor, styles.toolbarButton]}
        >
          CSV インポート
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,text/csv"
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
      </Box>

      {entries.length === 0 ? (
        <Typography sx={styles.emptyText}>
          許可 IP が未登録です。「行を追加」または「CSV インポート」で登録してください。
        </Typography>
      ) : (
        entries.map((entry, index) => {
          const showError = entry.cidr.trim() !== '' && !isValidIpv4Cidr(entry.cidr);
          return (
            <Box key={index} sx={styles.row}>
              <Box sx={styles.cidrCol}>
                <CustomTextfield
                  labelText={index === 0 ? 'IP / CIDR' : ''}
                  value={entry.cidr}
                  onChange={(e): void => updateRow(index, { cidr: e.target.value })}
                  placeholder="203.0.113.0/24"
                  inputWidth="100%"
                  fontSizeIs14px
                  formMb="0"
                />
                {showError && (
                  <Typography sx={styles.rowError}>IPv4 CIDR 形式で入力してください</Typography>
                )}
              </Box>
              <Box sx={styles.noteCol}>
                <CustomTextfield
                  labelText={index === 0 ? 'メモ（任意）' : ''}
                  value={entry.note ?? ''}
                  onChange={(e): void => updateRow(index, { note: e.target.value })}
                  placeholder="本社オフィス など"
                  inputWidth="100%"
                  fontSizeIs14px
                  formMb="0"
                />
              </Box>
              <IconButton
                onClick={(): void => removeRow(index)}
                size="small"
                sx={[styles.deleteButton, index === 0 && { marginTop: '28px' }]}
                aria-label="削除"
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
          );
        })
      )}

      <Typography sx={styles.countText}>
        {filledCount} / {SECURITY_IP_ALLOWLIST_MAX} 件
      </Typography>

      <Box sx={styles.footer}>
        <Button
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={[primaryButtonBackgroundColor, styles.saveButton]}
        >
          保存する
        </Button>
      </Box>

      <Dialog open={confirmOpen} onClose={(): void => setConfirmOpen(false)} sx={styles.dialog}>
        <Box sx={styles.dialogHeader}>
          <Typography sx={styles.dialogTitle}>ロックアウトの警告</Typography>
          <IconButton onClick={(): void => setConfirmOpen(false)} disabled={saving} size="small">
            <DeleteOutlineIcon sx={{ visibility: 'hidden' }} fontSize="small" />
          </IconButton>
        </Box>
        <Box sx={styles.warnBox}>
          現在のあなたの IP（{callerIp || '取得できませんでした'}）は許可リストに含まれていません。
          このまま保存すると、あなた自身がアクセスできなくなる恐れがあります。
        </Box>
        <Typography sx={styles.dialogText}>
          それでも保存しますか？（復旧が必要な場合はサポートへお問い合わせください）
        </Typography>
        <Box sx={styles.dialogActions}>
          <Button
            onClick={(): void => setConfirmOpen(false)}
            disabled={saving}
            sx={[tertiaryButtonBackgroundColor, styles.dialogButton]}
          >
            キャンセル
          </Button>
          <Button
            onClick={(): void => void save(true)}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={[primaryButtonBackgroundColor, styles.dialogButton]}
          >
            強制的に保存する
          </Button>
        </Box>
      </Dialog>
    </Box>
  );
};

export default IpRestrictionTab;
